import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Col, Container, Row, Label } from 'reactstrap';
import axios from 'axios';
import Swal from 'sweetalert2';

require("./assets/css/Video.css");

const VideoCallPublic = () => {
    const { token: urlToken } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    const [attendanceData, setAttendanceData] = useState(null);
    const [accessToken, setAccessToken] = useState(urlToken || null);
    const [error, setError] = useState(null);
    const [footerTitle, setFooterTitle] = useState("MEDR");
    const [footerUrl, setFooterUrl] = useState("https://medr.com.br/");
    const [logoUrl, setLogoUrl] = useState(null);
    const [companyName, setCompanyName] = useState("Video Atendimento");
    const [queuePosition, setQueuePosition] = useState(null);
    const [hasActiveCall, setHasActiveCall] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        department_id: '',
        reason: ''
    });

    const [departments, setDepartments] = useState([]);

    useEffect(() => {
        const domain = window.location.hostname;

        const updateCustomData = () => {
            try {
                const cachedData = localStorage.getItem(`custom_images_${domain}`);
                const cachedImages = cachedData ? JSON.parse(cachedData) : {};
                if (cachedImages.titleFooter) {
                    setFooterTitle(cachedImages.titleFooter);
                }
                if (cachedImages.companyUrlFooter) {
                    setFooterUrl(cachedImages.companyUrlFooter);
                }
                if (cachedImages.title) {
                    setCompanyName(cachedImages.title);
                }
                if (cachedImages.logoWhite || cachedImages.logo) {
                    setLogoUrl(cachedImages.logoWhite || cachedImages.logo);
                }
            } catch (error) {
                console.error('Erro ao carregar dados customizados:', error);
            }
        };

        updateCustomData();
        const interval = setInterval(updateCustomData, 500);
        const timeout = setTimeout(() => clearInterval(interval), 5000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    useEffect(() => {
        fetchDepartments();

        // If we have a token from URL, check status
        if (urlToken) {
            // Token is the attendance ID in this case
            checkAttendanceStatus(urlToken);
        }
    }, [urlToken]);

    useEffect(() => {
        let intervalId;

        if (isWaiting && attendanceData?.id && accessToken) {
            intervalId = setInterval(() => {
                checkAttendanceStatus(attendanceData.id);
            }, 5000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isWaiting, attendanceData, accessToken]);

    const fetchDepartments = async () => {
        try {
            // TODO: This endpoint needs to be created in the API
            // For now, we'll use a mock or the authenticated endpoint
            const response = await axios.get(
                `${process.env.REACT_APP_URL_API}video/departments/list-public`
            );

            if (response.data?.data) {
                setDepartments(response.data.data);
            }
        } catch (error) {
            console.error('Erro ao carregar departamentos:', error);
            // Use mock data for testing
            setDepartments([
                { id: 1, name: 'Atendimento Geral' },
                { id: 2, name: 'Suporte Técnico' },
                { id: 3, name: 'Comercial' }
            ]);
        }
    };

    const checkAttendanceStatus = async (attendanceId) => {
        if (!attendanceId) return;

        try {
            const url = `${process.env.REACT_APP_URL_API}video/status/${attendanceId}`;
            const params = accessToken ? { token: accessToken } : {};

            const response = await axios.get(url, { params });

            if (response.data?.data) {
                const data = response.data.data;
                setQueuePosition(data.queue_position);
                setHasActiveCall(data.has_active_call);

                // If there's an active call, redirect to video room
                if (data.has_active_call && data.call_id) {
                    navigate(`/video/sala/${attendanceId}?token=${accessToken}`);
                }

                // Update waiting state based on status
                if (['waiting', 'in_queue'].includes(data.status)) {
                    setIsWaiting(true);
                } else if (data.status === 'in_progress') {
                    setIsWaiting(true);
                }
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError('Atendimento não encontrado ou expirado.');
                setIsWaiting(false);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_URL_API}video/request`,
                formData
            );

            if (response.data?.data) {
                const data = response.data.data;
                setAttendanceData({
                    id: data.attendance_id,
                    protocol: data.protocol
                });
                setAccessToken(data.access_token);
                setQueuePosition(data.queue_position);
                setIsWaiting(true);

                // Store in sessionStorage for page refresh
                sessionStorage.setItem('video_attendance', JSON.stringify({
                    attendance_id: data.attendance_id,
                    access_token: data.access_token,
                    protocol: data.protocol
                }));
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Erro ao solicitar atendimento';
            setError(errorMsg);
            Swal.fire('Erro', errorMsg, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelAttendance = async () => {
        if (!attendanceData?.id || !accessToken) return;

        const result = await Swal.fire({
            title: 'Cancelar atendimento?',
            text: 'Você tem certeza que deseja cancelar a solicitação?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, cancelar',
            cancelButtonText: 'Não'
        });

        if (!result.isConfirmed) return;

        try {
            // TODO: Need a public cancel endpoint
            // For now, just reset the local state
            setIsWaiting(false);
            setAttendanceData(null);
            setAccessToken(null);
            setQueuePosition(null);
            setFormData({
                name: '',
                email: '',
                phone: '',
                department_id: '',
                reason: ''
            });

            sessionStorage.removeItem('video_attendance');

            Swal.fire('Cancelado', 'Sua solicitação foi cancelada.', 'info');
        } catch (error) {
            Swal.fire('Erro', 'Erro ao cancelar o atendimento.', 'error');
        }
    };

    const getStatusText = () => {
        if (hasActiveCall) {
            return 'Atendente disponível! Entrando na chamada...';
        }
        if (queuePosition) {
            return `Você está na posição ${queuePosition} da fila`;
        }
        return 'Aguardando atendente...';
    };

    const renderWaitingRoom = () => (
        <div className="video-waiting-room">
            <div className="video-waiting-animation mb-4">
                <i className="fa fa-video-camera fa-2x text-white"></i>
            </div>
            <h4 className="mb-3">{getStatusText()}</h4>

            {attendanceData?.protocol && (
                <div className="mb-3">
                    <small className="text-muted">Protocolo:</small>
                    <p className="mb-0"><strong>{attendanceData.protocol}</strong></p>
                </div>
            )}

            <p className="text-muted mb-4">
                Por favor, aguarde. Um atendente entrará em contato em breve.
            </p>

            {queuePosition && (
                <div className="mb-4">
                    <div className="d-flex justify-content-center gap-2">
                        <span className="badge bg-info fs-6 px-4 py-2">
                            <i className="fa fa-users me-2"></i>
                            Posição: {queuePosition}
                        </span>
                    </div>
                </div>
            )}

            <div className="video-info-card p-3 text-start mb-4" style={{ maxWidth: '300px', margin: '0 auto' }}>
                <p className="mb-1"><small className="text-muted">Nome:</small></p>
                <p className="mb-2"><strong>{formData.name}</strong></p>

                {formData.reason && (
                    <>
                        <p className="mb-1"><small className="text-muted">Motivo:</small></p>
                        <p className="mb-0">{formData.reason}</p>
                    </>
                )}
            </div>

            <button
                className="btn btn-outline-danger"
                onClick={handleCancelAttendance}
            >
                <i className="fa fa-times me-2"></i>
                Cancelar Solicitação
            </button>
        </div>
    );

    const renderForm = () => (
        <form onSubmit={handleSubmit}>
            <div className="mb-3">
                <Label className="form-label">Nome Completo *</Label>
                <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Digite seu nome completo"
                    required
                />
            </div>

            <div className="row">
                <div className="col-md-6">
                    <div className="mb-3">
                        <Label className="form-label">E-mail</Label>
                        <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Digite seu e-mail"
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-3">
                        <Label className="form-label">Telefone</Label>
                        <input
                            type="tel"
                            className="form-control"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(00) 00000-0000"
                        />
                    </div>
                </div>
            </div>

            <div className="mb-3">
                <Label className="form-label">Departamento *</Label>
                <select
                    className="form-select"
                    name="department_id"
                    value={formData.department_id}
                    onChange={handleChange}
                    required
                >
                    <option value="">Selecione o departamento</option>
                    {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>
                            {dept.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="mb-3">
                <Label className="form-label">Motivo do atendimento</Label>
                <textarea
                    className="form-control"
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    placeholder="Descreva brevemente o motivo do atendimento"
                    rows={3}
                />
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    {error}
                </div>
            )}

            <div className="mt-4">
                <button
                    type="submit"
                    className="btn btn-video-call btn-start w-100"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Solicitando...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-video-camera me-2"></i>
                            Solicitar Atendimento por Vídeo
                        </>
                    )}
                </button>
            </div>
        </form>
    );

    return (
        <React.Fragment>
            <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
                <div className="bg-overlay"></div>

                <div className="auth-page-content overflow-hidden pt-lg-5">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg={8} xl={6}>
                                <Card className="video-call-card">
                                    <Row className="g-0">
                                        <Col lg={12}>
                                            <div className="p-lg-5 p-4">
                                                <div className="text-center mb-4">
                                                    {logoUrl && (
                                                        <img
                                                            src={logoUrl}
                                                            alt="Logo"
                                                            style={{ maxHeight: '60px', marginBottom: '20px' }}
                                                        />
                                                    )}
                                                    <h4 className="text-primary">
                                                        <i className="fa fa-video-camera me-2"></i>
                                                        Atendimento por Vídeo
                                                    </h4>
                                                    <p className="text-muted">
                                                        {companyName}
                                                    </p>
                                                </div>

                                                {isWaiting ? renderWaitingRoom() : renderForm()}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </div>

                <footer className="footer">
                    <Container>
                        <Row>
                            <Col lg={12}>
                                <div className="text-center">
                                    <p className="mb-0 text-white-50">
                                        &copy; {new Date().getFullYear()}{" "}
                                        <a
                                            className="text-reset text-decoration-none"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={footerUrl}
                                        >
                                            {footerTitle}
                                        </a>
                                        . Todos Direitos Reservados
                                    </p>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </footer>
            </div>
        </React.Fragment>
    );
};

export default VideoCallPublic;
